/**
 * Savings & Shares Page
 * Professional informational product page for savings and share investment
 */
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ChevronRight,
  Home,
  PiggyBank,
  Users,
  ShieldCheck,
  TrendingUp,
  Wallet,
  BarChart3,
  Award,
  CheckCircle2,
  ArrowRight,
  Landmark,
  UserPlus,
  HandCoins,
} from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import axios from 'axios';

const CMS_API_URL = process.env.REACT_APP_API_BASE_URL_CMS || '';
const SAVING_HERO_UID = 'SAVING-HERO-SECTION';

/* ─── Hero ─── */
const SavingsSharesHero = () => {
  const [heroData, setHeroData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchHeroData = async () => {
      try {
        const response = await axios.post(
          `${CMS_API_URL}sections/get-by-uid`,
          { UID: SAVING_HERO_UID },
          {
            headers: { 'Content-Type': 'application/json' },
            timeout: 30000,
          }
        );

        if (response.data?.code === 1 && Array.isArray(response.data?.data) && response.data.data.length > 0) {
          const data = response.data.data[0];
          if (data.IsDeleted === false && data.IsPublished === true) {
            setHeroData({
              Title: data.Title,
              SubTitle: data.SubTitle,
              Description: data.Description,
            });
          }
        }
      } catch (error) {
        console.error('Failed to fetch saving hero data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchHeroData();
  }, []);

  if (!loading && !heroData) {
    return null;
  }

  if (loading) {
    return (
      <section className="bg-[#2f3951] py-5 md:py-5">
        <div className="container mx-auto px-6 md:px-8 lg:px-12">
          <div className="h-4 w-32 bg-white/20 rounded mb-3 animate-pulse" />
          <div className="h-8 w-48 bg-white/20 rounded mb-2 animate-pulse" />
          <div className="w-24 h-1 bg-white/20 mb-3 animate-pulse" />
          <div className="h-4 w-96 bg-white/20 rounded animate-pulse" />
        </div>
      </section>
    );
  }

  return (
    <section className="bg-[#2f3951] py-5 md:py-5">
      <div className="container mx-auto px-6 md:px-8 lg:px-12">
        {/* Breadcrumb */}
        <nav className="flex items-center text-white/80 text-sm mb-3">
          <Link to="/" className="flex items-center hover:text-[#ca9a37] transition-colors">
            <Home className="w-4 h-4 mr-1" />
            Home
          </Link>
          <ChevronRight className="w-4 h-4 mx-2" />
          <span className="text-[#ca9a37]">Savings & Shares</span>
        </nav>

        {/* hero section Title */}
        {heroData?.Title && (
          <h1 className="text-2xl md:text-4xl font-bold text-white mb-2">
            {heroData.Title}
          </h1>
        )}
        <div className="w-24 h-1 bg-[#ca9a37] mb-3"></div>

        {heroData?.Description && (
          <p className="text-base text-gray-200 max-w-2xl line-clamp-3">
            {heroData.Description.replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ').trim()}
          </p>
        )}
      </div>
    </section>
  );
};

/* ─── Introduction of Savings & Shares overview─── */
const IntroductionSection = () => {
  const [overviewData, setOverviewData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchOverview = async () => {
      try {
        const response = await axios.post(
          `${CMS_API_URL}overview/get`,
          { OverviewId: 1 },
          {
            headers: { 'Content-Type': 'application/json' },
            timeout: 30000,
          }
        );

        if (response.data?.code === 1 && Array.isArray(response.data?.data) && response.data.data.length > 0) {
          setOverviewData(response.data.data[0]);
        }
      } catch (error) {
        console.error('Failed to fetch savings overview:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchOverview();
  }, []);

  const stripHtml = (html) => {
    if (!html) return '';
    return html.replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ').trim();
  };

  if (!loading && !overviewData) {
    return null;
  }

  if (loading) {
    return (
      <section className="py-7 bg-white">
        <div className="container mx-auto px-6 md:px-8 lg:px-12">
          <div className="max-w-3xl mx-auto text-center">
            <div className="h-4 w-24 bg-gray-200 rounded mb-3 animate-pulse mx-auto" />
            <div className="h-8 w-64 bg-gray-200 rounded mb-4 animate-pulse mx-auto" />
            <div className="w-16 h-1 bg-gray-200 mx-auto mb-6 animate-pulse" />
            <div className="h-4 w-full bg-gray-200 rounded mb-6 animate-pulse" />
            <div className="flex flex-col md:flex-row gap-6 justify-center">
              <div className="h-12 w-48 bg-gray-200 rounded-lg animate-pulse" />
              <div className="h-12 w-48 bg-gray-200 rounded-lg animate-pulse" />
            </div>
            <div className="h-4 w-full bg-gray-200 rounded mt-6 animate-pulse" />
          </div>
        </div>
      </section>
    );
  }

  const RegularIcon = overviewData?.RegularIcon ? LucideIcons[overviewData.RegularIcon] : null;
  const SharesIcon = overviewData?.SharesIcon ? LucideIcons[overviewData.SharesIcon] : null;

  return (
    <section className="py-7 bg-white">
      <div className="container mx-auto px-6 md:px-8 lg:px-12">
        <div className="max-w-3xl mx-auto text-center">
          {overviewData?.SubTitle && (
            <span className="text-[#ca9a37] font-semibold text-sm uppercase tracking-wide">
              {overviewData.SubTitle}
            </span>
          )}
          {overviewData?.Title && (
            <h2 className="text-2xl md:text-3xl font-bold text-[#2f3951] mt-2 mb-4">
              {overviewData.Title}
            </h2>
          )}
          <div className="w-16 h-1 bg-[#ca9a37] mx-auto mb-6"></div>
          {overviewData?.Description1 && (
            <p className="text-gray-600 leading-relaxed">
              {stripHtml(overviewData.Description1)}
            </p>
          )}
          {(overviewData?.RegularSaving || overviewData?.MemberShares) && (
            <div className="flex flex-col md:flex-row gap-6 mt-8 justify-center">
              {overviewData?.RegularSaving && (
                <div className="flex items-center gap-3 bg-[#f8f1e5] rounded-lg px-6 py-4">
                  {RegularIcon && <RegularIcon className="w-6 h-6 text-[#ca9a37]" />}
                  <span className="font-medium text-[#2f3951]">{overviewData.RegularSaving}</span>
                </div>
              )}
              {overviewData?.MemberShares && (
                <div className="flex items-center gap-3 bg-[#f8f1e5] rounded-lg px-6 py-4">
                  {SharesIcon && <SharesIcon className="w-6 h-6 text-[#ca9a37]" />}
                  <span className="font-medium text-[#2f3951]">{overviewData.MemberShares}</span>
                </div>
              )}
            </div>
          )}
          {overviewData?.Description2 && (
            <p className="text-gray-500 text-sm mt-6">
              {stripHtml(overviewData.Description2)}
            </p>
          )}
        </div>
      </div>
    </section>
  );
};

/* ─── Info Card ─── */
const InfoCard = ({ icon: Icon, title, items }) => (
  <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
    <div className="flex items-center gap-3 mb-4">
      <div className="w-10 h-10 rounded-lg bg-[#ca9a37]/10 flex items-center justify-center">
        <Icon className="w-5 h-5 text-[#ca9a37]" />
      </div>
      <h4 className="font-semibold text-[#2f3951]">{title}</h4>
    </div>
    <ul className="space-y-2">
      {items.map((item, i) => (
        <li key={i} className="flex items-start text-gray-600 text-sm">
          <CheckCircle2 className="w-4 h-4 text-[#ca9a37] mr-2 flex-shrink-0 mt-0.5" />
          {item}
        </li>
      ))}
    </ul>
  </div>
);

/* ─── Regular Savings Section ─── */
const SavingsAndSharesSection = () => {
  const [investments, setInvestments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchInvestments = async () => {
      try {
        const response = await axios.post(
          `${CMS_API_URL}investment/published`,
          {},
          {
            headers: { 'Content-Type': 'application/json' },
            timeout: 30000,
          }
        );

        if (response.data?.code === 1 && Array.isArray(response.data?.data)) {
          const validItems = response.data.data
            .filter(item => item.IsDeleted === false && item.IsPublished === true)
            .sort((a, b) => (a.SortOrder || 0) - (b.SortOrder || 0))
            .slice(0, 2);
          setInvestments(validItems);
        }
      } catch (error) {
        console.error('Failed to fetch investments:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchInvestments();
  }, []);

  const stripHtml = (html) => {
    if (!html) return '';
    return html.replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ').trim();
  };

  const parseJsonArray = (val) => {
    if (!val) return [];
    if (Array.isArray(val)) return val;
    try {
      const parsed = JSON.parse(val);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  };

  if (!loading && investments.length === 0) {
    return null;
  }

  if (loading) {
    return (
      <section className="py-7 bg-gray-50">
        <div className="container mx-auto px-6 md:px-8 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-8">
            {[1, 2].map((i) => (
              <div key={i} className="bg-white rounded-2xl p-6 md:p-8 shadow-sm border border-gray-100 h-96 animate-pulse" />
            ))}
          </div>
        </div>
      </section>
    );
  }

  // Map by SortOrder: 1 → Main Service, 2 → Investment Ownership
  const mainService = investments.find(i => i.SortOrder === 1) || investments[0];
  const investmentOwnership = investments.find(i => i.SortOrder === 2) || investments[1];

  const MainServiceIcon = mainService?.Icon ? LucideIcons[mainService.Icon] : null;
  const InvestmentIcon = investmentOwnership?.Icon ? LucideIcons[investmentOwnership.Icon] : null;

  return (
    <section className="py-7 bg-gray-50">
      <div className="container mx-auto px-6 md:px-8 lg:px-12">
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Main Service */}
          <div className="bg-white rounded-2xl p-6 md:p-8 shadow-sm border border-gray-100 h-full flex flex-col">
            {mainService && (
              <div className="flex items-center gap-3 mb-3">
                <div className="w-12 h-12 rounded-xl bg-[#ca9a37]/10 flex items-center justify-center">
                  {MainServiceIcon && <MainServiceIcon className="w-6 h-6 text-[#ca9a37]" />}
                </div>
                <span className="text-[#ca9a37] font-semibold text-sm uppercase tracking-wide">Main Service</span>
              </div>
            )}
            {mainService?.Title && (
              <h2 className="text-2xl md:text-3xl font-bold text-[#2f3951] mb-4">{mainService.Title}</h2>
            )}
            <div className="w-16 h-1 bg-[#ca9a37] mb-6"></div>
            {mainService?.Description && (
              <p className="text-gray-600 leading-relaxed mb-8">
                {stripHtml(mainService.Description)}
              </p>
            )}

            <div className="grid sm:grid-cols-2 gap-4">
              {parseJsonArray(mainService?.KeyFeatureList).length > 0 && (
                <InfoCard
                  icon={TrendingUp}
                  title={mainService?.KeyFeatures || 'Key Features'}
                  items={parseJsonArray(mainService.KeyFeatureList)}
                />
              )}
              {parseJsonArray(mainService?.BenefitList).length > 0 && (
                <InfoCard
                  icon={ShieldCheck}
                  title={mainService?.Benefit || 'Benefits'}
                  items={parseJsonArray(mainService.BenefitList)}
                />
              )}
            </div>

            {parseJsonArray(mainService?.WhoCanList).length > 0 && (
              <div className="mt-auto bg-gray-100 rounded-xl p-5 border border-gray-200">
                <div className="flex items-center gap-3 mb-2">
                  <Users className="w-5 h-5 text-[#ca9a37]" />
                  <h4 className="font-semibold text-gray-800">{mainService?.WhoCan || 'Who Can Join'}</h4>
                </div>
                <ul className="space-y-1 text-gray-600 text-sm">
                  {parseJsonArray(mainService.WhoCanList).map((item, idx) => (
                    <li key={idx} className="flex items-center gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-[#ca9a37]" /> {item}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Investment Ownership */}
          <div className="bg-white rounded-2xl p-6 md:p-8 shadow-sm border border-gray-100 h-full flex flex-col">
            {investmentOwnership && (
              <div className="flex items-center gap-3 mb-3">
                <div className="w-12 h-12 rounded-xl bg-[#ca9a37]/10 flex items-center justify-center">
                  {InvestmentIcon && <InvestmentIcon className="w-6 h-6 text-[#ca9a37]" />}
                </div>
                <span className="text-[#ca9a37] font-semibold text-sm uppercase tracking-wide">Investment Ownership</span>
              </div>
            )}
            {investmentOwnership?.Title && (
              <h2 className="text-2xl md:text-3xl font-bold text-[#2f3951] mb-4">{investmentOwnership.Title}</h2>
            )}
            <div className="w-16 h-1 bg-[#ca9a37] mb-6"></div>
            {investmentOwnership?.Description && (
              <p className="text-gray-600 leading-relaxed mb-8">
                {stripHtml(investmentOwnership.Description)}
              </p>
            )}

            {investmentOwnership?.InvestmentShare && (
              <div className="inline-block bg-[#ca9a37]/10 text-[#ca9a37] font-bold text-sm px-4 py-2 rounded-lg mb-6">
                {investmentOwnership.InvestmentShare}
              </div>
            )}

            <div className="grid sm:grid-cols-2 gap-4">
              {parseJsonArray(investmentOwnership?.KeyFeatureList).length > 0 && (
                <InfoCard
                  icon={BarChart3}
                  title={investmentOwnership?.KeyFeatures || 'Key Features'}
                  items={parseJsonArray(investmentOwnership.KeyFeatureList)}
                />
              )}
              {parseJsonArray(investmentOwnership?.BenefitList).length > 0 && (
                <InfoCard
                  icon={Award}
                  title={investmentOwnership?.Benefit || 'Benefits'}
                  items={parseJsonArray(investmentOwnership.BenefitList)}
                />
              )}
            </div>

            {parseJsonArray(investmentOwnership?.WhoCanList).length > 0 && (
              <div className="mt-auto bg-gray-100 rounded-xl p-5 border border-gray-200">
                <div className="flex items-center gap-3 mb-2">
                  <Users className="w-5 h-5 text-[#ca9a37]" />
                  <h4 className="font-semibold text-gray-800">{investmentOwnership?.WhoCan || 'Who Can Invest'}</h4>
                </div>
                <ul className="space-y-1 text-gray-600 text-sm">
                  {parseJsonArray(investmentOwnership.WhoCanList).map((item, idx) => (
                    <li key={idx} className="flex items-center gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-[#ca9a37]" /> {item}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

/* ─── How It Works ─── */
const HowItWorksSection = () => {
  const [benefitData, setBenefitData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBenefit = async () => {
      try {
        const response = await axios.post(
          `${CMS_API_URL}benefit/get`,
          { BenefitId: 1 },
          {
            headers: { 'Content-Type': 'application/json' },
            timeout: 30000,
          }
        );

        if (response.data?.code === 1 && Array.isArray(response.data?.data) && response.data.data.length > 0) {
          const data = response.data.data[0];
          if (data.IsPublished === true) {
            setBenefitData(data);
          }
        }
      } catch (error) {
        console.error('Failed to fetch benefit (How It Works):', error);
      } finally {
        setLoading(false);
      }
    };

    fetchBenefit();
  }, []);

  const stripHtml = (html) => {
    if (!html) return '';
    return html.replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ').trim();
  };

  const parseServiceCards = (cardsJson) => {
    if (!cardsJson) return [];
    try {
      const parsed = JSON.parse(cardsJson);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  };

  if (!loading && !benefitData) {
    return null;
  }

  if (loading) {
    return (
      <section className="py-7 bg-[#2f3951]">
        <div className="container mx-auto px-6 md:px-8 lg:px-12">
          <div className="text-center mb-10">
            <div className="h-4 w-24 bg-gray-600 rounded mb-3 animate-pulse mx-auto" />
            <div className="h-8 w-64 bg-gray-600 rounded mb-4 animate-pulse mx-auto" />
            <div className="w-16 h-1 bg-gray-600 mx-auto mb-6 animate-pulse" />
          </div>
          <div className="grid md:grid-cols-5 gap-6">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="text-center">
                <div className="w-16 h-16 mx-auto rounded-full bg-gray-600 mb-4 animate-pulse" />
                <div className="h-4 w-24 bg-gray-600 rounded mb-2 animate-pulse mx-auto" />
                <div className="h-3 w-full bg-gray-600 rounded animate-pulse" />
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  const cards = parseServiceCards(benefitData?.ServiceCards);

  return (
    <section className="py-7 bg-[#2f3951]">
      <div className="container mx-auto px-6 md:px-8 lg:px-12">
        <div className="text-center mb-10">
          {benefitData?.SubTitle && (
            <span className="text-[#ca9a37] font-semibold text-sm uppercase tracking-wide">{benefitData.SubTitle}</span>
          )}
          {benefitData?.Title && (
            <h2 className="text-2xl md:text-3xl font-bold text-white mt-2 mb-4">{benefitData.Title}</h2>
          )}
          <div className="w-16 h-1 bg-[#ca9a37] mx-auto"></div>
        </div>

        {cards.length > 0 && (
          <div className="grid md:grid-cols-5 gap-6 relative">
            {/* Connecting line (desktop) */}
            <div className="hidden md:block absolute top-8 left-[10%] right-[10%] h-0.5 bg-[#ca9a37]/30"></div>

            {cards.map((step, i) => {
              const StepIcon = step.icon ? LucideIcons[step.icon] : null;
              return (
                <div key={i} className="relative text-center z-10">
                  <div className="w-16 h-16 mx-auto rounded-full bg-[#ca9a37] flex items-center justify-center text-white font-bold text-xl mb-4 shadow-lg">
                    {i + 1}
                  </div>
                  {StepIcon && <StepIcon className="w-6 h-6 text-[#ca9a37] mx-auto mb-2" />}
                  <h4 className="font-semibold text-white mb-1">{step.title}</h4>
                  <p className="text-gray-300 text-sm leading-snug">{stripHtml(step.description)}</p>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
};

/* ─── Why Choose Us ─── */
const WhyChooseUsSection = () => {
  const [benefitData, setBenefitData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBenefit = async () => {
      try {
        const response = await axios.post(
          `${CMS_API_URL}benefit/get`,
          { BenefitId: 2 },
          {
            headers: { 'Content-Type': 'application/json' },
            timeout: 30000,
          }
        );

        if (response.data?.code === 1 && Array.isArray(response.data?.data) && response.data.data.length > 0) {
          const data = response.data.data[0];
          if (data.IsPublished === true) {
            setBenefitData(data);
          }
        }
      } catch (error) {
        console.error('Failed to fetch benefit (Why Choose Us):', error);
      } finally {
        setLoading(false);
      }
    };

    fetchBenefit();
  }, []);

  const stripHtml = (html) => {
    if (!html) return '';
    return html.replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ').trim();
  };

  const parseServiceCards = (cardsJson) => {
    if (!cardsJson) return [];
    try {
      const parsed = JSON.parse(cardsJson);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  };

  if (!loading && !benefitData) {
    return null;
  }

  if (loading) {
    return (
      <section className="py-7 bg-gray-50">
        <div className="container mx-auto px-6 md:px-8 lg:px-12">
          <div className="text-center mb-5">
            <div className="h-4 w-24 bg-gray-200 rounded mb-3 animate-pulse mx-auto" />
            <div className="h-8 w-64 bg-gray-200 rounded mb-4 animate-pulse mx-auto" />
            <div className="w-16 h-1 bg-gray-200 mx-auto mb-6 animate-pulse" />
          </div>
          <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-6">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 text-center">
                <div className="w-12 h-12 mx-auto rounded-full bg-gray-200 mb-4 animate-pulse" />
                <div className="h-4 w-24 bg-gray-200 rounded mb-2 animate-pulse mx-auto" />
                <div className="h-3 w-full bg-gray-200 rounded animate-pulse" />
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  const cards = parseServiceCards(benefitData?.ServiceCards);

  return (
    <section className="py-7 bg-gray-50">
      <div className="container mx-auto px-6 md:px-8 lg:px-12">
        <div className="text-center mb-5">
          {benefitData?.SubTitle && (
            <span className="text-[#ca9a37] font-semibold text-sm uppercase tracking-wide">{benefitData.SubTitle}</span>
          )}
          {benefitData?.Title && (
            <h2 className="text-2xl md:text-3xl font-bold text-[#2f3951] mt-2 mb-4">{benefitData.Title}</h2>
          )}
          <div className="w-16 h-1 bg-[#ca9a37] mx-auto"></div>
        </div>

        {cards.length > 0 && (
          <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-6">
            {cards.map((card, i) => {
              const CardIcon = card.icon ? LucideIcons[card.icon] : null;
              return (
                <div key={i} className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 text-center hover:-translate-y-1 transition-transform">
                  <div className="w-12 h-12 mx-auto rounded-full bg-[#ca9a37]/10 flex items-center justify-center mb-4">
                    {CardIcon && <CardIcon className="w-6 h-6 text-[#ca9a37]" />}
                  </div>
                  <h4 className="font-semibold text-[#2f3951] mb-2">{card.title}</h4>
                  <p className="text-gray-500 text-sm leading-relaxed">{stripHtml(card.description)}</p>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
};

/* ─── CTA ─── */
const CTASection = () => {
  const [ctaData, setCtaData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCta = async () => {
      try {
        const response = await axios.post(
          `${CMS_API_URL}overview/get`,
          { OverviewId: 2 },
          {
            headers: { 'Content-Type': 'application/json' },
            timeout: 30000,
          }
        );

        if (response.data?.code === 1 && Array.isArray(response.data?.data) && response.data.data.length > 0) {
          const data = response.data.data[0];
          if (data.IsPublished === true) {
            setCtaData(data);
          }
        }
      } catch (error) {
        console.error('Failed to fetch CTA overview:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchCta();
  }, []);

  const stripHtml = (html) => {
    if (!html) return '';
    return html.replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ').trim();
  };

  if (!loading && !ctaData) {
    return null;
  }

  if (loading) {
    return (
      <section className="py-4 bg-white">
        <div className="container mx-auto px-6 md:px-8 lg:px-12">
          <div className="bg-[#2f3951] rounded-2xl p-8 md:p-12 text-center max-w-4xl mx-auto">
            <div className="h-8 w-64 bg-gray-600 rounded mb-4 animate-pulse mx-auto" />
            <div className="w-16 h-1 bg-gray-600 mx-auto mb-6 animate-pulse" />
            <div className="h-4 w-full max-w-xl bg-gray-600 rounded mb-8 animate-pulse mx-auto" />
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <div className="h-12 w-48 bg-gray-600 rounded-lg animate-pulse" />
              <div className="h-12 w-48 bg-gray-600 rounded-lg animate-pulse" />
            </div>
          </div>
        </div>
      </section>
    );
  }

  const PrimaryIcon = ctaData?.RegularIcon ? LucideIcons[ctaData.RegularIcon] : null;
  const SecondaryIcon = ctaData?.SharesIcon ? LucideIcons[ctaData.SharesIcon] : null;

  return (
    <section className="py-4 bg-white">
      <div className="container mx-auto px-6 md:px-8 lg:px-12">
        <div className="bg-[#2f3951] rounded-2xl p-8 md:p-12 text-center max-w-4xl mx-auto">
          {ctaData?.Title && (
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">{ctaData.Title}</h2>
          )}
          <div className="w-16 h-1 bg-[#ca9a37] mx-auto mb-6"></div>
          {ctaData?.Description1 && (
            <p className="text-gray-200 max-w-xl mx-auto mb-8 leading-relaxed">
              {stripHtml(ctaData.Description1)}
            </p>
          )}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            {ctaData?.RegularSaving && ctaData?.RegularLink && (
              <Link
                to={ctaData.RegularLink}
                className="inline-flex items-center justify-center gap-2 bg-[#ca9a37] hover:bg-[#b88a2f] text-white font-semibold px-6 py-3 rounded-lg transition-colors"
              >
                {PrimaryIcon && <PrimaryIcon className="w-5 h-5" />}
                {ctaData.RegularSaving}
              </Link>
            )}
            {ctaData?.MemberShares && ctaData?.MemberShareLink && (
              <Link
                to={ctaData.MemberShareLink}
                className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white font-semibold px-6 py-3 rounded-lg transition-colors border border-white/20"
              >
                {SecondaryIcon && <SecondaryIcon className="w-5 h-5" />}
                {ctaData.MemberShares}
              </Link>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

/* ─── Footer Note ─── */
const FooterNote = () => {
  const [footerData, setFooterData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFooter = async () => {
      try {
        const response = await axios.post(
          `${CMS_API_URL}overview/get`,
          { OverviewId: 2 },
          {
            headers: { 'Content-Type': 'application/json' },
            timeout: 30000,
          }
        );

        if (response.data?.code === 1 && Array.isArray(response.data?.data) && response.data.data.length > 0) {
          const data = response.data.data[0];
          if (data.IsPublished === true) {
            setFooterData(data);
          }
        }
      } catch (error) {
        console.error('Failed to fetch footer overview:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchFooter();
  }, []);

  const stripHtml = (html) => {
    if (!html) return '';
    return html.replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ').trim();
  };

  if (!loading && !footerData) {
    return null;
  }

  if (loading) {
    return (
      <div className="bg-gray-50 border-t border-gray-200 py-6">
        <div className="container mx-auto px-6 md:px-8 lg:px-12 text-center">
          <div className="h-4 w-full max-w-2xl bg-gray-200 rounded animate-pulse mx-auto" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 border-t border-gray-200 py-6">
      <div className="container mx-auto px-6 md:px-8 lg:px-12 text-center">
        {footerData?.Description2 && (
          <p className="text-gray-500 text-sm flex items-center justify-center gap-2">
            <ShieldCheck className="w-4 h-4 text-[#ca9a37]" />
            {stripHtml(footerData.Description2)}
          </p>
        )}
      </div>
    </div>
  );
};

/* ─── Page ─── */
const SavingsSharesPage = () => (
  <div>
    <SavingsSharesHero />
    <IntroductionSection />
    <SavingsAndSharesSection />
    <HowItWorksSection />
    <WhyChooseUsSection />
    <CTASection />
    <FooterNote />
  </div>
);

export default SavingsSharesPage;
